import React from "react"
import { Link, graphql } from "gatsby"
import styled from "styled-components"
import Img from "gatsby-image"
import slugify from "../lib/slugify"
import moment from "moment"

import Metadata from "../components/Metadata"
import Circuit from "../components/Circuit"
import ExerciseThumbnail from "../components/ExerciseThumbnail"
import EquipmentThumbnail from "../components/EquipmentThumbnail"
import ResourceThumbnail from "../components/ResourcesThumbnail"
import ReadTime from "../components/ReadTime"
import GridPitch from "../components/GridPitch"
import FooterPitch from "../components/FooterPitch"

import { List } from "./exercises"

export const extractText = (text, type) => {
  // const example = [
  //   {
  //     type: "text",
  //     text: { content: "10 Challenging Hanging Core Exercises", link: null },
  //     annotations: {
  //       bold: false,
  //       italic: false,
  //       strikethrough: false,
  //       underline: false,
  //       code: false,
  //       color: "default",
  //     },
  //     plain_text: "10 Challenging Hanging Core Exercises",
  //     href: null,
  //   },
  // ]
  //

  const items = text?.map(obj => {
    let textItem = obj.text?.content

    // Don't create html processing for headings to create correct table of contents
    if (!type?.includes("heading") && type !== "title") {
      /* Styles */
      if (obj.annotations?.bold) textItem = "<strong>" + textItem + "</strong>"
      if (obj.annotations?.italic)
        textItem = "<span style='font-style: italic'>" + textItem + "</span>"
      if (obj.annotations?.underline)
        textItem =
          "<span style='text-decoration: underline'>" + textItem + "</span>"

      /* State */
      if (obj.text?.link)
        textItem = `<a href="${obj.text.link.url}">${textItem}</a>`
    }

    return textItem
  })

  return items?.join(" ")
}

export const handlePlugin = json => {
  // const plugin = code.match(/\(.*?\)/g).map(x => x.replace(/[()]/g, ""))
  const code = JSON.parse(json)

  if (code.plugin === "exercise") {
    return (
      <ExerciseContainer>
        <ExerciseThumbnail {...code.data} autoplay showAffiliate />
      </ExerciseContainer>
    )
  }

  if (code.plugin === "equipment") {
    return (
      <EquipmentContainer>
        {code.data.equipment?.map(name => (
          <EquipmentThumbnail name={name} />
        ))}
      </EquipmentContainer>
    )
  }

  if (code.plugin === "circuit") {
    return <Circuit {...code.data} />
  }
}

export const pageQuery = graphql`
  query ($featureImage: String!) {
    featureImage: file(id: { eq: $featureImage }) {
      childImageSharp {
        fluid(maxWidth: 900, quality: 90) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    relatedImages: allFile(
      filter: { id: { regex: "/editorial-feature-image/" } }
    ) {
      edges {
        node {
          id
          childImageSharp {
            fixed(cropFocus: CENTER, width: 400, height: 300, quality: 90) {
              ...GatsbyImageSharpFixed
            }
          }
        }
      }
    }
  }
`

const Resource = ({
  pageContext: {
    image,
    excerpt,
    title,
    featureImage,
    blocks,
    contents,
    readTime,
    author,
    reviewer,
    exercises,
    related,
    createdTime,
    lastEditedTime,
  },
  data,
  location,
}) => {
  const formattedTitle = extractText(title[title.type], title.type)

  const scrollTo = id => {
    const el = document.getElementById(id)

    el?.scrollIntoView({
      behavior: "smooth",
      inline: "start",
    })
  }

  return (
    <>
      <Metadata
        title={formattedTitle}
        description={extractText(excerpt[excerpt.type])}
        url={`https://programme.app/resources/${slugify(formattedTitle)}`}
        image={data.featureImage.childImageSharp.fluid.src}
      />
      <Container>
        <Head>
          <h1>{formattedTitle}</h1>
          <div>
            <p>{extractText(excerpt[excerpt.type])}</p>
            <ReadTime>{readTime} min read</ReadTime>
            <Stats>
              {author ? (
                <StatWithLink href={author.url}>
                  <img alt={author.name} src={author.image} />
                  <Data>
                    <div>Written by</div>
                    <div>{author.name}</div>
                  </Data>
                </StatWithLink>
              ) : null}
              {reviewer ? (
                <StatWithLink href={reviewer.url}>
                  <img src={reviewer.image} />
                  <Data>
                    <div>Reviewed by</div>
                    <div>{reviewer.name}</div>
                  </Data>
                </StatWithLink>
              ) : (
                <Stat>
                  <Data>
                    <div>Published on</div>
                    <div>{moment(createdTime).format("DD/MM/YY")}</div>
                  </Data>
                </Stat>
              )}
              <Stat>
                <Data>
                  <div>Last updated</div>
                  <div>{moment(lastEditedTime).format("DD/MM/YY")}</div>
                </Data>
              </Stat>
            </Stats>
          </div>
        </Head>
        {exercises.length ? (
          <List>
            {exercises.slice(0, 5).map(m => (
              <ExerciseThumbnail autoplay key={m?.id} {...m} />
            ))}
            <GridPitch />
            {exercises.slice(5).map(m => (
              <ExerciseThumbnail autoplay key={m?.id} {...m} />
            ))}
          </List>
        ) : (
          <FeatureImage>
            <Img
              fluid={data?.featureImage?.childImageSharp?.fluid}
              alt={formattedTitle + " Feature Image"}
              placeholderStyle={{ visibility: "hidden" }}
            />
          </FeatureImage>
        )}
        <TableOfContents>
          <span>In This Resource</span>
          <ul>
            {contents.map(i => (
              <ContentsItem indent={i.type === "heading_3"}>
                <div
                  onClick={() =>
                    scrollTo(`${i.text.toLowerCase().replace(/ /g, "-")}`)
                  }
                >
                  {i.text}
                </div>
              </ContentsItem>
            ))}
          </ul>
        </TableOfContents>
        <Body>
          {blocks.map(block => {
            const text = extractText(block[block.type].text, block.type)

            switch (block.type) {
              case "heading_2":
                return (
                  <h2
                    id={text.toLowerCase().replace(/ /g, "-")}
                    dangerouslySetInnerHTML={{ __html: text }}
                  />
                )
              case "heading_3":
                return (
                  <h3
                    id={text.toLowerCase().replace(/ /g, "-")}
                    dangerouslySetInnerHTML={{ __html: text }}
                  />
                )
              case "paragraph":
                return <p dangerouslySetInnerHTML={{ __html: text }} />
              case "bulleted_list_item":
              case "numbered_list_item":
                return <li dangerouslySetInnerHTML={{ __html: text }} />
              case "code":
                return handlePlugin(text)
              default:
                return <p dangerouslySetInnerHTML={{ __html: text }} />
            }
          })}
        </Body>
        <Signature>
          <p>
            If you enjoyed this resource you can find more below or try{" "}
            <a href="https://apps.apple.com/us/app/programme/id1554594236/">
              Programme
            </a>
            , a fitness app that plans every workout for you – based on your
            progress, equipment and lifestyle.
          </p>
          <p>
            This resource was written by <a href={author.url}>{author.name}</a>
            {reviewer && (
              <span>
                {" "}
                and reviewed by <a href={reviewer.url}>{reviewer.name}</a>
              </span>
            )}
            . {author.bio}
          </p>
          <Link to={author.url}>
            <h3>{author.name}</h3>
            <img src={author.signature} alt="Signature" />
          </Link>
        </Signature>
        {related.length ? (
          <RelatedResources>
            <h2>Keep Reading</h2>
            <ResourcesRow>
              {related.map(r => (
                <ResourceThumbnail
                  key={r.id}
                  {...r}
                  featureImage={
                    data.relatedImages.edges.filter(
                      i => i.node.id === r.featureImage
                    )[0]
                  }
                />
              ))}
            </ResourcesRow>
          </RelatedResources>
        ) : null}
      </Container>
      <FooterPitch />
    </>
  )
}

const Container = styled.div`
  padding: 0 20px;
`

export const Head = styled.div`
  display: flex;
  flex-direction: row;
  padding: 12em ${({ padding }) => (padding ? "2em" : 0)} 1em;

  h1 {
    color: var(--white);
    font-weight: normal;
    margin-top: 0;
    min-width: ${({ withFilters }) =>
      withFilters ? "calc(220px + (100% - 220px) / 2)" : "50%"};
    max-width: ${({ withFilters }) =>
      withFilters ? "calc(220px + (100% - 220px) / 2)" : "50%"};
    padding-right: 10%;
  }

  & > div {
    padding 0 1em;
  }

  h2 {
    &:first-child {
      margin-top: 0;
    }
    color: var(--white);
    max-width: 700px;
    font-size: 1.2rem;
  }

  p {
    color: var(--light-grey);
    margin-top: 0;
    max-width: 700px;
    font-size: 1.2rem;
  }

  @media (max-width: 56em) {
    flex-direction: column;
    padding: 6em ${({ padding }) => (padding ? "1em" : 0)} 0em;

    & > div {
      padding: 0;
    }

    h1 {
      max-width: 100%;
    }

    p {
      font-size: 1rem;
    }
  }
`

export const Stats = styled.div`
  margin-top: 8em;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;

  @media (max-width: 48em) {
    margin-top: 4em;
  }

  @media (max-width: 400px) {
    // flex-direction: column;
  }
`

const StatStyles = `
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-right: 2em;
  margin-bottom: 1em;

  img {
    margin-right: 0.5em;
    width: 2em;
    height: 2em;
    border-radius: 2em;
  }

  &:last-child {
    margin-right: 0;
  }

  @media (max-width: 48em) {
    margin-right: 1em;
  }
`

export const Stat = styled.div`
  ${StatStyles}
`

export const StatWithLink = styled.a`
  ${StatStyles}
`

export const Data = styled.div`
  div:first-child {
    font-size: 16px;
    color: var(--light-grey);
  }
  div:last-child {
    font-size: 18px;
    color: var(--white);
  }
`

const FeatureImage = styled.div`
  img {
    border-radius: 3em;
  }

  @media (max-width: 48em) {
    img {
      border-radius: 2em;
    }
  }
`

export const TableOfContents = styled.div`
  width: 640px;
  max-width: 100%;
  margin: 4em auto;
  background-color: #1a1a1a;
  padding: 20px 20px 8px;
  border-radius: 8px;

  span {
    color: var(--white);
  }

  div {
    cursor: pointer;
    color: var(--light-grey);

    &:hover {
      text-decoration: underline;
    }
  }

  @media (max-width: 48em) {
    margin: 2em auto;
  }
`

export const ContentsItem = styled.li`
  text-indent: ${props => (props.indent ? "30px" : "0")};
  list-style: none;
`

export const ExerciseContainer = styled.div`
  max-width: 640px;
  margin: 2em auto;
  width: 100%;
`

export const EquipmentContainer = styled.div`
  max-width: 640px;
  margin: 2em auto;
  display: flex;
  flex-direction: horizontal;
  overflow: visible;
  flex-wrap: wrap;
`

export const Body = styled.div`
  margin: 2em auto;
  // background: white;

  > h2,
  > h3,
  > p,
  > ul,
  > ol,
  > li {
    max-width: 640px !important;
    margin-left: auto;
    margin-right: auto;
    color: var(--white);
  }

  p,
  li {
    color: var(--light-grey);
  }

  a {
    color: var(--white);
    text-decoration: underline;
  }
`

export const Signature = styled.div`
  border-top: 2px solid var(--dark-grey);
  width: 640px;
  max-width: 100%;
  margin: 4em auto 2em;

  p {
    margin: 8px 0 0 0;
    color: var(--light-grey);
  }

  a {
    color: white;
  }

  h3 {
    margin: 1em 0 0;
    color: var(--white);
  }

  img {
    height: 60px;
    margin-top: -1.5em;
    margin-left: 1.5em;
  }
`

const RelatedResources = styled.div`
  margin: 4em auto 2em;
`

const ResourcesRow = styled.div`
  display: grid;
  grid-gap: 10px;
  grid-template-columns: 1fr 1fr;
  margin-top: 1em;

  @media (max-width: 48em) {
    grid-template-columns: 1fr;
  }
`

export default Resource
